import React, { Component, MouseEvent, createRef } from 'react';
import { Animated } from 'components/common/Animated/Animated';
import classnames from 'classnames';
import intl from 'react-intl-universal';

import confirmImg from 'assets/images/notification-confirm.svg';
import successImg from 'assets/images/notification-success.svg';
import errorImg from 'assets/images/notification-error.svg';
import closeImg from 'assets/images/close.svg';

import { enqueue } from './notificationQueue';

import './Notification.scss';
import { sanitizeHtml } from 'utils/sanitizeHtml';

const showDelay = 5000;
const animationInDuration = 300;
const animationOutDuration = 500;

export enum NotificationTypes {
  CONFIRM,
  SUCCESS,
  ERROR
}

export type Props = {
  type: NotificationTypes;
  title: string;
  isTitleHTML?: boolean;
  hideIcon?: boolean;
  submitButtonTitle?: string;
  cancelButtonTitle?: string;
  onSubmitClick?(): void;
  onCancelClick?(): void;
  /** Supplied by NotificationHost: settles the promise returned by create(). */
  onResolved?(value: boolean): void;
  /** Supplied by NotificationHost: drops this entry from the queue once hidden. */
  onClosed?(): void;
};

export type State = {
  isOpen: boolean;
  isAnimated: boolean;
};

/**
 * Await Notification.create for CONFIRM, where the promise settles true or false with
 * the user's answer. SUCCESS and ERROR now settle false when they auto-hide; before this
 * refactor their promise never settled at all, so awaiting one hung forever.
 */
export class Notification extends Component<Props, State> {
  private hasResolved = false;
  private autoHideTimer?: number;
  private closeTimer?: number;

  public ref = createRef<HTMLButtonElement>();

  public state = {
    isOpen: false,
    isAnimated: true,
  };

  /** Settles create()'s promise exactly once, whichever path closed the dialog. */
  private settle = (value: boolean) => {
    if (this.hasResolved) {
      return;
    }
    this.hasResolved = true;
    if (this.props.onResolved) {
      this.props.onResolved(value);
    }
  }

  public componentDidMount() {
    const { type } = this.props;
    if (type === NotificationTypes.CONFIRM) {
      // The dimming used to be applied by create() before rendering. It belongs with the
      // dialog's own lifecycle, so that it is impossible to leave behind.
      document.getElementById('root')!.classList.add('disabledArea');
      this.ref.current!.focus();
    }
    this.setState({ isOpen: true });
    if (
      type === NotificationTypes.SUCCESS ||
      type === NotificationTypes.ERROR
    ) {
      this.autoHideTimer = window.setTimeout(
        () => {
          if (this.state.isOpen) {
            this.handleHideDialog();
          }
        },
        showDelay
      );
    }
  }

  /**
   * Unchanged signature, so all 55 call sites keep working. What changed is the
   * mechanism: this used to createElement a div, append it to #notifications and call
   * ReactDOM.render into it — a second React root per notification. It now pushes onto a
   * queue that NotificationHost renders through a portal.
   *
   * Await it for CONFIRM, where it settles true or false. SUCCESS and ERROR resolve false
   * when they auto-hide; previously their promise never settled at all.
   */
  /**
   * One Notification is constructed and destroyed for every notification the application
   * raises, across 55 call sites, so an uncleared timer here is the highest-churn leak in
   * the codebase. Dismissing a notification by hand used to leave its auto-hide timer
   * running to fire against a dead instance.
   */
  public componentWillUnmount() {
    window.clearTimeout(this.autoHideTimer);
    window.clearTimeout(this.closeTimer);
  }

  /**
   * A static method rather than a static arrow property, so that it has a named type. The
   * lint config names it under no-floating-promises' allowForKnownSafeCalls — 97 call sites
   * correctly do not await it — and an arrow assigned to a property has no name to match.
   * The body never used `this`, so nothing else changes.
   */
  public static async create(props: Props): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      enqueue(props, resolve);
    });
  }

  public handleHideDialog = () => {
    if (this.props.type === NotificationTypes.CONFIRM) {
      const rootDiv = document.getElementById('root');
      rootDiv!.classList.remove('disabledArea');
    }

    this.setState({
      isAnimated: false
    });

    this.closeTimer = window.setTimeout(
      () => {
        this.setState({ isOpen: false });
        // Closing without an explicit answer counts as a negative one. Resolving here as
        // well as on the buttons is what stops a dismissed CONFIRM leaving its caller
        // awaiting a promise that never settles.
        this.settle(false);
        this.props.onClosed!();
      },
      animationOutDuration + 1
    );
  }

  public handleCancelClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (this.props.onCancelClick) {
      this.props.onCancelClick();
    }
    this.settle(false);
    this.handleHideDialog();
  }

  public handleSubmitClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (this.props.onSubmitClick) {
      this.props.onSubmitClick();
    }
    this.settle(true);
    this.handleHideDialog();
  }

  public onCloseIconClick = (e: MouseEvent<HTMLImageElement>) => {
    e.preventDefault();

    this.handleHideDialog();
  }

  public renderIcon = () => {
    const { type } = this.props;

    const image = type === NotificationTypes.CONFIRM ? confirmImg :
      type === NotificationTypes.SUCCESS ? successImg :
      errorImg;

    return (
      <img className="Notification_icon" src={image} alt="icon" />
    );
  }

  public renderTitle = () => {
    const { title, isTitleHTML } = this.props;

    if (isTitleHTML) {
      return (
        <p className="Notification_title" dangerouslySetInnerHTML={{ __html: sanitizeHtml(title) }} />
      );
    }

    return (
      <p className="Notification_title">{title}</p>
    );
  }

  public renderCloseIcon = () => (
    <img
      src={closeImg}
      alt={intl.get('generals.close')}
      title={intl.get('generals.close')}
      onClick={this.onCloseIconClick}
      className="Notification_close"
    />
  )

  public renderFooter = () => {
    const {
      submitButtonTitle = intl.get('notifications.ok'),
      cancelButtonTitle = intl.get('notifications.cancel')
    } = this.props;
    return (
      <footer className="Notification_footer">
        <button className="Notification_submit" title={intl.get('notifications.ok')} ref={this.ref} onClick={this.handleSubmitClick}>{submitButtonTitle}</button>
        <button className="Notification_cancel" title={intl.get('notifications.cancel')} onClick={this.handleCancelClick}>{cancelButtonTitle}</button>
      </footer>
    );
  }

  public render() {
    const { type, hideIcon } = this.props;

    const { isAnimated } = this.state;

    const containerClassnames = classnames(
      type === NotificationTypes.CONFIRM && 'Notification_container__confirm',
      type === NotificationTypes.SUCCESS && 'Notification_container__success',
      type === NotificationTypes.ERROR && 'Notification_container__error'
    );

    return (
        <Animated
          animationIn="fadeInRight"
          animationOut="fadeOutRight"
          isVisible={isAnimated}
          animationInDuration={animationInDuration}
          animationOutDuration={animationOutDuration}
          className={containerClassnames}
        >
          <section className="Notification_content">
            {!hideIcon! && this.renderIcon()}
            {this.renderTitle()}
            {this.renderCloseIcon()}
          </section>

          {type === NotificationTypes.CONFIRM && this.renderFooter()}
        </Animated>
    );
  }
}
